/**
 * Creates a queue of tasks to be run by requestIdleCallback. Mostly based on the example provided
 * by MDN here:
 * https://developer.mozilla.org/en-US/docs/Web/API/Background_Tasks_API
 * @param  {Number} options.timeout - if the specified timeout is reached before the callback is
 * executed within an idle period, a task is queued to execute it.
 * @return {Object}
 */
var createTaskQueue = function createTaskQueue() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _options$timeout = options.timeout,
      timeout = _options$timeout === void 0 ? 2500 : _options$timeout;
  /**
   * taskList is an Array of objects, each representing one task waiting to be run.
   */

  var taskList = [];
  /**
   * taskHandle is an ID referencing the requestIdleCallback task currently being processed (can be
   * used with window.cancelIdleCallback()).
   */

  var taskHandle = null;
  /**
   * Shim for RequestIdelCallback/CancelIdleCallback based on the following GIST:
   * https://gist.github.com/paullewis/55efe5d6f05434a96c36
   * Unlike other API's used in this library, RequestIdleCallback will be shimmed/polyfilled in
   * order to include Safari.
   */

  var _requestIdleCallback = window.requestIdleCallback || function (cb) {
    return setTimeout(function () {
      var start = Date.now();
      cb({
        didTimeout: false,
        timeRemaining: function timeRemaining() {
          return Math.max(0, 50 - (Date.now() - start));
        }
      });
    }, 1);
  }; // eslint-disable-next-line no-unused-vars


  var _cancelIdleCallback = window.cancelIdleCallback || function (id) {
    return clearTimeout(id);
  };
  /**
   * Runs the enqueued tasks. This idle callback handler will get called when the browser
   * determines there's enough idle time available or the timeout expires.
   * @param  {IdleDeadline} deadline - an object describing the amount of time available and
   * whether or not the callback has been run because the timeout period expired
   */


  var runTaskQueue = function runTaskQueue(deadline) {
    /**
     * loop will continue as long as
     * - there's time left
     * - the timeout limit was reached
     * - as long as there are tasks in the task list
     */
    while ((deadline.timeRemaining() > 0 || deadline.didTimeout) && taskList.length) {
      var task = taskList.shift();
      task.handler(task.data);
    }
    /**
     * if there are still tasks left in the list after the while loop, call requestIdleCallback()
     * again so that tasks can continue to process the next time there's idle time available.
     */


    if (taskList.length) {
      taskHandle = _requestIdleCallback(runTaskQueue, {
        timeout: timeout
      });
    } else {
      taskHandle = 0;
    }
  };
  /**
   * enqueues tasks for future execution.
   * @param  {Function} taskHandler - a function which will be called to handle the task.
   * @param  {Object} taskData - An object passed into the task handler as an input parameter, to
   * allow the task to receive custom data.
   */


  var enqueueTask = function enqueueTask(taskHandler, taskData) {
    taskList.push({
      handler: taskHandler,
      data: taskData
    });
    /**
     * If there isn't an idle callback yet, call requestIdleCallback() to create one.
     */

    if (!taskHandle) {
      taskHandle = _requestIdleCallback(runTaskQueue, {
        timeout: timeout
      });
    }
  };

  return {
    enqueueTask: enqueueTask
  };
};

export { createTaskQueue };
